import { resolveInboxChannel } from "./resolveInboxChannel.util";
import {
	findMeAmongSenders,
	resolveMyDefaultSender,
	type MyDefaultSender,
} from "./resolveMyDefaultSender.util";

import type { Conversation } from "@/pages/Private/pages/Apollo/schema/get-people";
import type { User } from "@/pages/Public/pages/Login/schema/login";

import type { SenderRecipientOption } from "../components/SenderRecipientDropdown";

import type { DraftRecipient, DraftSender } from "./inboxDraft.types";

export interface DraftDefaults {
	from?: DraftSender;
	to?: DraftRecipient;
	/**
	 * Set when the conversation's sender can no longer send (logged out, mailbox deleted or
	 * disconnected, user deleted) and the draft fell back to the current user (PREC-4312, E5).
	 * `userId` and `name` are null when the conversation does not say who it was.
	 */
	goneSender?: { userId: number | null; name: string | null };
	/** The thread's own sender option, so AI drafts restored onto it keep its mailbox (E4). */
	threadSender?: SenderRecipientOption;
}

export interface ResolveDefaultDraftDefaultsParams {
	conversations: Conversation[];
	listPersonId: number | undefined;
	prospect: { email?: string | null; linkedinUrl?: string | null } | null | undefined;
	senderOptions: SenderRecipientOption[];
	recipientOptions: SenderRecipientOption[];
	sendingUsers: User[];
	currentUser: User | null | undefined;
	connectionCheckDone: boolean;
	connectedUserIds: Set<number>;
}

const buildRecipient = (
	type: "email" | "linkedin",
	value: string | null | undefined,
	recipientOptions: SenderRecipientOption[]
): DraftRecipient | undefined => {
	const option = recipientOptions.find((o) => o.type === type);

	if (option) {
		return { type, label: option.label, value: option.value ?? "" };
	}

	if (!value) {
		return undefined;
	}

	return { type, label: value, value };
};

const draftSenderFromOption = (
	option: SenderRecipientOption,
	fallbackUserId: number | undefined
): DraftSender => ({
	userId: option.userId ?? fallbackUserId ?? 0,
	type: option.type,
	label: option.label,
	emailId: option.emailId,
	emailAddress: option.emailAddress,
});

const draftRecipientFromOption = (option: SenderRecipientOption): DraftRecipient => ({
	type: option.type,
	label: option.label,
	value: option.value ?? "",
});

/** The composer's option for the current user's default sender, matched by identity. */
const findMyOption = (
	pick: MyDefaultSender,
	senderOptions: SenderRecipientOption[]
): SenderRecipientOption | undefined => {
	if (pick.kind === "linkedin") {
		return senderOptions.find((o) => o.type === "linkedin" && o.userId === pick.userId);
	}

	if (pick.kind === "email") {
		return senderOptions.find(
			(o) => o.type === "email" && o.userId === pick.userId && o.emailId === pick.emailId
		);
	}

	return undefined;
};

const resolveMyDefaults = (
	params: ResolveDefaultDraftDefaultsParams
): Pick<DraftDefaults, "from" | "to"> => {
	const { prospect, senderOptions, recipientOptions, sendingUsers, currentUser } = params;
	const pick = resolveMyDefaultSender({
		me: findMeAmongSenders(sendingUsers, currentUser?.id),
		prospectHasLinkedinUrl: !!prospect?.linkedinUrl,
		prospectHasEmail: !!prospect?.email,
		connectionCheckDone: params.connectionCheckDone,
		connectedUserIds: params.connectedUserIds,
	});
	const option = findMyOption(pick, senderOptions);

	if (!option) {
		return {};
	}

	const value = option.type === "linkedin" ? prospect?.linkedinUrl : prospect?.email;

	return {
		from: draftSenderFromOption(option, currentUser?.id),
		to: buildRecipient(option.type, value, recipientOptions),
	};
};

const personName = (user: Conversation["user"] | undefined): string | null => {
	const name = [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim();

	return name || null;
};

/**
 * Resolves the default `from` / `to` for a freshly-opened draft (PREC-4312).
 *
 *   1. Ongoing conversation → the channel, sender and recipient of the last exchange, even when the
 *      sender is a colleague (delegated to {@link resolveInboxChannel}; E4). Sending as someone else
 *      is confirmed at Send (`useSenderConfirmGate`), not avoided here.
 *   2. The conversation's sender can no longer send → the current user's own default, with
 *      `goneSender` set so the composer can say so (E5). A LinkedIn thread is judged only once the
 *      connection check has built the LinkedIn options, and nothing before the senders loaded.
 *   3. No conversation → the current user's own default ({@link resolveMyDefaultSender}), never a
 *      colleague. With no channel of their own the composer stays on "Select sender".
 */
export const resolveDefaultDraftDefaults = (
	params: ResolveDefaultDraftDefaultsParams
): DraftDefaults => {
	const resolved = resolveInboxChannel(
		params.conversations,
		params.listPersonId,
		params.senderOptions,
		params.recipientOptions
	);

	if (resolved.channelType && resolved.senderOption) {
		return {
			from: draftSenderFromOption(resolved.senderOption, params.currentUser?.id),
			to: resolved.recipientOption ? draftRecipientFromOption(resolved.recipientOption) : undefined,
			threadSender: resolved.senderOption,
		};
	}

	if (!resolved.channelType) {
		return resolveMyDefaults(params);
	}

	const optionsComplete =
		params.sendingUsers.length > 0 &&
		(resolved.channelType === "email" || params.connectionCheckDone);

	if (!optionsComplete) {
		return {};
	}

	return {
		...resolveMyDefaults(params),
		goneSender: {
			userId: resolved.basisUserId ?? null,
			name: personName(resolved.basisUser),
		},
	};
};
